// Minting the reCAPTCHA token the candidate link writes carry.
//
// Loaded lazily and only when a site key is configured. Loading
// `enterprise.js?render=undefined` still defines `window.grecaptcha.enterprise`,
// and `execute()` then hangs for ever, so a missing key has to mean no script at
// all rather than a script we cannot use.

const SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY as string | undefined;

// Long enough for a phone on a slow connection, short enough that a candidate
// gets a retryable refusal rather than a button that never responds.
const READY_TIMEOUT_MS = 10_000;

let loading: Promise<boolean> | null = null;

function load(): Promise<boolean> {
  if (!SITE_KEY) return Promise.resolve(false);
  if (loading) return loading;

  loading = new Promise<boolean>((resolve) => {
    const grecaptcha = (window as never as { grecaptcha?: { enterprise?: unknown } })
      .grecaptcha;
    if (grecaptcha?.enterprise) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${SITE_KEY}`;
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
  return loading;
}

/**
 * A token for this action, or an empty string when we cannot mint one.
 *
 * Empty is not silently allowed anywhere: the API refuses a write without a token,
 * so this returning '' surfaces as a refusal the candidate can retry rather than
 * as a write that quietly skipped the check.
 */
export async function captchaToken(action: string): Promise<string> {
  const ready = await load();
  if (!ready || !SITE_KEY) return '';

  const enterprise = (
    window as never as {
      grecaptcha?: {
        enterprise?: {
          ready(cb: () => void): void;
          execute(key: string, opts: { action: string }): Promise<string | null>;
        };
      };
    }
  ).grecaptcha?.enterprise;
  if (!enterprise) return '';

  try {
    // The script's load event fires before the enterprise API finishes wiring
    // itself up: `grecaptcha.enterprise` is already an object while `execute`
    // is still undefined, so calling it here throws and the candidate is told
    // their token is missing. `ready` is the only signal that it is usable, and
    // it is bounded because a callback that never fires would hang the upload
    // rather than fail it.
    const usable = await Promise.race([
      new Promise<boolean>((resolve) => enterprise.ready(() => resolve(true))),
      new Promise<boolean>((resolve) => setTimeout(() => resolve(false), READY_TIMEOUT_MS)),
    ]);
    if (!usable) return '';
    // Enterprise resolves with null, rather than rejecting, when the key is not
    // valid for this domain, so a catch alone would let null through a signature
    // that promises a string.
    return (await enterprise.execute(SITE_KEY, { action })) || '';
  } catch {
    return '';
  }
}
